// Port of ios/Sources/ViewModels/CatchUp.swift.
//
// "Ponerse al día" (2026-10-09, Slack's Catch up): the unread conversations as a deck of cards —
// swipe right to mark read, left for later, tap to open. Twin of Android's `CatchUp`.
//
// The Swift file reads `ChannelListSection`/`DisplayChannel` (the channel-list port) and
// `SystemMessageDerivation.isSystem`. Those belong to other families, so this module takes the
// few fields it reads as small structural interfaces — any channel-list row type that has them
// fits — and carries its own copy of the one-line `isSystem` rule.
import type { PostRecord } from './records';

export const maxMessages = 6;

/** The fields of a `DisplayChannel` the deck reads. */
export interface CatchUpRow {
  id: string;
  isUnread: boolean;
  mentionCount: number;
  isMuted: boolean;
  lastPostAt: number;
  /** Non-null for an archived channel or a deactivated DM partner (`ChannelInactiveReason`). */
  inactive: unknown | null;
}

/** The field of a `ChannelListSection` the deck reads. */
export interface CatchUpSection<Row extends CatchUpRow> {
  rows: readonly Row[];
}

/**
 * Every unmuted conversation asking for attention, once, mentions first, then the most
 * recently active. Archived channels and deactivated partners stay out.
 */
export function queue<Row extends CatchUpRow>(sections: readonly CatchUpSection<Row>[]): Row[] {
  const seen = new Set<string>();
  return sections
    .flatMap((s) => s.rows)
    .filter((row) => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    })
    .filter((row) => (row.isUnread || row.mentionCount > 0) && !row.isMuted && row.inactive == null)
    .sort((lhs, rhs) => {
      const lhsMention = lhs.mentionCount > 0;
      const rhsMention = rhs.mentionCount > 0;
      if (lhsMention !== rhsMention) return lhsMention ? -1 : 1;
      return rhs.lastPostAt - lhs.lastPostAt;
    });
}

/**
 * `SystemMessageDerivation.isSystem`: a `system_*` type, except `system_ephemeral` — ephemeral
 * posts are messages with content, an author and interactive controls, and the only thing
 * "system" about them is the type string the server happens to stamp on them.
 */
function isSystem(type: string | null | undefined): boolean {
  if (type == null || type === '') return false;
  return type.startsWith('system_') && type !== 'system_ephemeral';
}

/**
 * What a card shows, oldest first: the messages after `lastViewedAt`, at most `max` of the
 * newest; when none are cached yet, the last three stand in so the card is never blank.
 */
export function cardMessages(
  newestFirst: readonly PostRecord[],
  lastViewedAt: number,
  max: number = maxMessages,
): PostRecord[] {
  const visible = newestFirst.filter((p) => p.deleteAt === 0 && !isSystem(p.type));
  const unread = visible.filter((p) => p.createAt > lastViewedAt);
  const chosen = unread.length === 0 ? visible.slice(0, 3) : unread.slice(0, max);
  return chosen.reverse();
}
